import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import hashSum from 'hash-sum'
import { provider } from '@/app-bootstrapping/SetUpApollo'
import { deepClone, dotAccess, findProperty, merge, sleep } from '@/utils/tools'

const perPage = 250
// Each page is a heavy query; unbounded parallel pages exhaust app server memory.
const maxConcurrentPageRequests = 3

export interface DataDescriber<T> {
  title: string // Column name
  extraction: (data: T) => string
}

export interface SheetDescriber<T> {
  title: string // Sheet name
  columns: DataDescriber<T>[]
}

export interface TableDescriber<T> {
  title: string // Name of the document getting saved
  sheets: SheetDescriber<T>[]
}

export interface Sheet {
  title: string
  rows: Record<string, string>[]
}

export interface Table {
  title: string
  sheets: Sheet[]
}

export interface DataMap {
  perPageVarName: string // Variable name that specifies the number of elements per page
  pageVarName: string // Variable name that specifies the page number
  listPath: string // Dot path to the array containing the data rows
}

export interface QueryOptions {
  query: TypedDocumentNode
  variables: any
}

// Enum for supported file formats
export enum ExportFormat {
  EXCEL = 'excel',
  CSV = 'csv',
  JSON = 'json',
}

// Interface for export files
export interface ExportFile {
  filename: string
  content: Blob
}

async function queryData(
  page: number,
  query: TypedDocumentNode,
  variables: Record<string, any>,
  dataMap: DataMap,
  clientName: string,
) {
  const client = provider.clients[clientName]

  return client.query({
    query,
    variables: {
      ...variables,
      [dataMap.pageVarName]: page,
    },
  })
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let nextIndex = 0
  let failed = false

  const worker = async () => {
    while (!failed && nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      try {
        results[index] = await mapper(items[index])
      } catch (error) {
        failed = true
        throw error
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))

  return results
}

export async function retrieveData(options: QueryOptions, dataMap: DataMap, clientName: string) {
  const { query, variables } = options
  const client = provider.clients[clientName]
  const vars = deepClone(variables)
  vars[dataMap.pageVarName] = 1

  let { data } = await client.query({
    query,
    variables: vars,
  })

  const total: number = findProperty('paginatorInfo', data)?.total ?? 0

  if (total > vars[dataMap.perPageVarName]) {
    vars[dataMap.perPageVarName] = perPage
    const numberOfPages = Math.ceil(total / perPage)
    const queryCurry = (page: number) => queryData(page + 1, query, vars, dataMap, clientName)
    const results = await mapWithConcurrency(
      [...Array(numberOfPages).keys()],
      maxConcurrentPageRequests,
      queryCurry,
    )
    data = results.reduce((acc, res) => merge(acc, res.data), data)
  }

  return data
}

const ID_TITLE_KEY = 'Id'
function removeDuplicates(data: Record<string, string>[]) {
  const seen = {}
  const useId = !!data?.[0]?.[ID_TITLE_KEY]

  return data.filter(
    (row) => {
      const key = useId ? row[ID_TITLE_KEY] : hashSum(row)
      // eslint-disable-next-line no-return-assign
      return seen.hasOwnProperty(key) ? false : (seen[key] = true)
    }, // Removes duplicates
  )
}

function dataStreamToSheet<T>(
  data: Record<string, any>,
  tableDescription: TableDescriber<T>,
  dataMap: DataMap,
) {
  const stream: T[] = dotAccess(data, dataMap.listPath)

  const table: Table = {
    title: tableDescription.title,
    sheets: [],
  }

  for (const sheetDescription of tableDescription.sheets) {
    const sheet: Sheet = {
      title: sheetDescription.title,
      rows: removeDuplicates(
        stream.map((row: T) =>
          sheetDescription.columns.reduce(
            (rowAccumulator, columnDescription) => ({
              ...rowAccumulator,
              [columnDescription.title]: columnDescription.extraction(row),
            }),
            {},
          ),
        ),
      ),
    }

    table.sheets.push(sheet)
  }

  return table
}

function escapeXML(str: string | null): string {
  if (str?.replace) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
  }
  return ''
}

function escapeCSV(data: string | null): string {
  if (typeof data !== 'string') return `${data}`
  return `"${data.replace(/"/g, '""')}"`
}

// Function to generate Excel export file
function generateExcelExportFile(table: Table): ExportFile {
  let xml =
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" ' +
    'xmlns:o="urn:schemas-microsoft-com:office:office" ' +
    'xmlns:x="urn:schemas-microsoft-com:office:excel" ' +
    'xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n'

  // For each sheet
  for (const sheet of table.sheets) {
    // Get the headers from the first row
    const headers = Object.keys(sheet.rows[0] || {})

    if (headers.length === 0) {
      // eslint-disable-next-line no-continue
      continue
    }

    xml += `<Worksheet ss:Name="${escapeXML(sheet.title)}">\n`
    xml += '<Table>\n'

    // Write header row
    xml += '<Row>\n'
    for (const header of headers) {
      xml += `<Cell><Data ss:Type="String">${escapeXML(header)}</Data></Cell>\n`
    }
    xml += '</Row>\n'

    // Write data rows
    for (const row of sheet.rows) {
      xml += '<Row>\n'
      for (const header of headers) {
        const cellData = row[header] || ''
        xml += `<Cell><Data ss:Type="String">${escapeXML(cellData)}</Data></Cell>\n`
      }
      xml += '</Row>\n'
    }

    xml += '</Table>\n'
    xml += '</Worksheet>\n'
  }

  xml += '</Workbook>'

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' })
  const filename = `${table.title}.xls`

  return { filename, content: blob }
}

// Function to generate CSV export files
function generateCSVExportFiles(table: Table): ExportFile[] {
  const files: ExportFile[] = []

  for (const sheet of table.sheets) {
    const headers: string[] = Object.keys(sheet.rows[0] || {})
    const csvRows: string[] = []

    // Add headers
    csvRows.push(headers.map(escapeCSV).join(','))

    // Add data rows
    for (const row of sheet.rows) {
      const rowData: string[] = headers.map((header) => escapeCSV(row[header]))
      csvRows.push(rowData.join(','))
    }

    const csvContent = csvRows.join('\r\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const filename =
      table.sheets.length > 1 ? `${table.title}-${sheet.title}.csv` : `${table.title}.csv`

    files.push({ filename, content: blob })
  }

  return files
}

// Function to generate JSON export file. This one just takes the data stream as json does not support the sheet
// method that well
function generateJSONExportFile(dataStream: { [p: string]: any }, title: string): ExportFile {
  const jsonContent = JSON.stringify(dataStream, null, 2)
  const blob = new Blob([jsonContent], { type: 'application/json' })
  const filename = `${title}.json`

  return { filename, content: blob }
}

// Function to download export files
async function downloadExportFiles(exportFiles: ExportFile | ExportFile[]) {
  const files = Array.isArray(exportFiles) ? exportFiles : [exportFiles]

  // Helper function to download a single file
  function downloadFile(file: ExportFile): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const url = URL.createObjectURL(file.content)
        const a = document.createElement('a')
        a.href = url
        a.download = file.filename

        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

  // Function to download files sequentially with a delay
  async function downloadFilesSequentially(fileList: ExportFile[]) {
    for (const file of fileList) {
      await downloadFile(file)
      // Introduce a delay between downloads to prevent browser overload
      await sleep(100)
    }
  }

  await downloadFilesSequentially(files)
}

// Main export function
export async function dataExport<T>(
  tableDescription: TableDescriber<T>,
  options: QueryOptions,
  dataMap: DataMap,
  clientName = 'publicApi',
  format: ExportFormat = ExportFormat.EXCEL,
) {
  const client = provider.clients[clientName]
  if (!client) return

  const data: { [p: string]: any } = await retrieveData(options, dataMap, clientName)
  const tableData = dataStreamToSheet<T>(data, tableDescription, dataMap)

  let exportFiles: ExportFile | ExportFile[]

  switch (format) {
    case ExportFormat.EXCEL:
      exportFiles = generateExcelExportFile(tableData)
      break
    case ExportFormat.CSV:
      exportFiles = generateCSVExportFiles(tableData)
      break
    case ExportFormat.JSON:
      exportFiles = generateJSONExportFile(data, tableData.title)
      break
    default:
      throw new Error(`Unsupported export format: ${format}`)
  }

  // We might want to wrap this in a catch block as it can throw errors or return the export files and let the client
  // figure out when and where to download
  await downloadExportFiles(exportFiles)
}
